import React from 'react';

import { ExclamationTriangleOutlineIcon } from '@/components/Icon';

const OUTER_BORDER = 'border-[#3F4145]';
const CELL_BORDER = 'border-[#B4B4B4]';
const LABEL_CELL = 'bg-[#F7F8FA]';
const LABEL_TEXT = 'text-[9px] sm280:text-[8px] whitespace-nowrap text-gray-12';

interface VehicleRegistrationGuideProps {
  className?: string;
}

interface OwnerNameExample {
  text: string;
  isCorrect: boolean;
  note?: string;
}

const OWNER_NAME_EXAMPLES: OwnerNameExample[] = [
  { text: '주식회사 직트럭(상품용)', isCorrect: true },
  { text: '(주)직트럭(상품용)', isCorrect: false, note: '* 주식회사 로 입력' },
  { text: '주식회사 직트럭', isCorrect: false, note: '* (상품용)까지 입력' },
  { text: '주식회사 직 트럭(상품용)', isCorrect: false, note: '* 띄어쓰기는 정확하게' },
];

const CircledNumber = ({ number }: { number: string }) => <span className="mr-[2px]">{number}</span>;

const CorrectMark = () => <span className="w-4 h-4 border-2 border-[#4F7FC1] rounded-full shrink-0" />;

const WrongMark = () => (
  <svg width={14} height={14} viewBox="0 0 14 14" fill="none" className="shrink-0">
    <path d="M3 2.4 9.6 11.6M11 2.4 4.4 11.6" stroke="#C4161C" strokeWidth={2.4} strokeLinecap="round" />
  </svg>
);

const VehicleRegistrationGuide = ({ className = '' }: VehicleRegistrationGuideProps) => {
  return (
    <div className={className}>
      <div className={`overflow-hidden bg-gray-0 border-[1.5px] rounded-xl ${OUTER_BORDER}`}>
        <div className={`flex items-center justify-center min-h-[28px] border-b-[1.5px] ${OUTER_BORDER}`}>
          <span className="text-[15px] sm280:text-[14px] font-bold tracking-[0.24em] -mr-[0.24em] text-gray-10">
            자동차등록증
          </span>
        </div>

        <div className={`flex border-b ${CELL_BORDER}`}>
          <div
            className={`flex items-center w-[27%] min-h-[16px] px-2 border-r ${LABEL_TEXT} ${CELL_BORDER} ${LABEL_CELL}`}
          >
            <CircledNumber number="⑥" />
            차대번호
          </div>
          <div className={`flex items-center flex-1 px-3 border-r ${CELL_BORDER}`}>
            <span className="w-full h-[6px] rounded-full bg-gray-3" />
          </div>
          <div className={`flex items-center w-[10.4%] px-1 ${LABEL_TEXT} ${LABEL_CELL}`}>
            <CircledNumber number="⑦" />
            원동
          </div>
        </div>

        <div className={`flex border-b ${CELL_BORDER}`}>
          <div
            className={`flex items-center w-[27%] min-h-[17px] px-2 border-r ${LABEL_TEXT} ${CELL_BORDER} ${LABEL_CELL}`}
          >
            <CircledNumber number="⑧" />
            사용본거지
          </div>
          <div className="flex-1" />
        </div>

        <div className="flex">
          <div
            className={`flex items-center justify-center w-[8.7%] px-1 border-r ${LABEL_TEXT} ${CELL_BORDER} ${LABEL_CELL}`}
          >
            소유자
          </div>
          <div className="flex-1 min-w-0">
            <div className={`relative flex border-b min-h-[52px] ${CELL_BORDER}`}>
              <div
                className={`flex items-center w-[28%] px-2 sm280:px-1 border-r text-[11px] sm280:text-[10px] font-semibold whitespace-nowrap text-gray-10 bg-[#EBF4FD] ${CELL_BORDER}`}
              >
                <CircledNumber number="⑨" />
                성명(명칭)
              </div>
              <div
                className={`flex items-center justify-center w-[48.4%] px-1 border-r text-[12px] sm280:text-[11px] font-bold whitespace-nowrap text-red bg-[#FFF5F4] ${CELL_BORDER}`}
              >
                주식회사 직트럭(상품용)
              </div>
              <div
                className={`flex items-center w-[15%] px-[1px] border-r text-[7px] sm280:text-[6px] leading-[10px] text-gray-12 break-keep ${CELL_BORDER}`}
              >
                <span>
                  <CircledNumber number="⑩" />
                  주민(법인) 등록번호
                </span>
              </div>
              <div className="flex-1" />
              <span className="absolute left-0 -top-[1px] w-[77.1%] h-[calc(100%+2px)] border-2 border-[#DA3845] pointer-events-none" />
            </div>
            <div className="flex min-h-[19px]">
              <div className={`flex items-center w-[25.5%] px-2 border-r ${LABEL_TEXT} ${CELL_BORDER} ${LABEL_CELL}`}>
                <CircledNumber number="⑪" />
                주소
              </div>
              <div className="flex-1" />
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 mt-3 rounded-2xl bg-secondaryYellow-0">
        <div className="flex items-center gap-2 mb-4">
          <ExclamationTriangleOutlineIcon width={16} height={16} color="#D97706" />
          <span className="text-[15px] sm280:text-[14px] font-bold text-[#B45309]">입력 시 주의사항</span>
        </div>
        <ul className="flex flex-col gap-4">
          {OWNER_NAME_EXAMPLES.map(({ text, isCorrect, note }) => (
            <li key={text} className="flex items-center gap-2">
              <div className="flex items-center justify-between w-[62%] gap-[6px] shrink-0 px-[10px] sm280:px-2 py-[7px] bg-gray-0 border border-[#EFEFEF] rounded-[10px]">
                <span className="text-[12px] sm280:text-[11px] leading-[18px] font-medium text-gray-9 break-keep">
                  {text}
                </span>
                {isCorrect ? <CorrectMark /> : <WrongMark />}
              </div>
              <span className="flex-1 min-w-0 text-[11px] sm280:text-[10px] leading-[15px] text-gray-7 break-keep">
                {note}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default VehicleRegistrationGuide;
